import React, { Component } from 'react';
import { withCookies, Cookies } from 'react-cookie';
import { instanceOf } from 'prop-types';
import { withRouter } from 'react-router-dom';

// Components
import Menu from '@components/Menu';
import EmpresaSelect from '@components/EmpresaSelect';
import UserContext from '@components/context/userContext';
import { NotificationManager } from 'react-notifications';

// Services
import menuService from '@services/menu';
import empresasService from '@services/empresas';

import './menu-permissions.css';

// Define module categories (centralized source)
const getModuleCategories = () => ({
  'Contabilidade': {
        'accounting:statistics': 'Estatísticas',
        'accounting:turno': 'Declaração de Caixa',
        'accounting:consumption/vehicles': 'Consumos por matrícula',
        'accounting:stocks': 'Existências em stock',
        'accounting:estimates': 'Sondagens',
        'accounting:anmc': 'Listagem ENSE',
        'accounting:saft': 'SAFT-PT v1.4',
    },
    'Ferramentas': {
        'tools:clients': 'Clientes (beta)',
        'tools:customers': 'Clientes',
        'tools:providers': 'Fornecedores',
        'tools:partners': 'Parceiros',
        'tools:products': 'Produtos',
        'tools:agenda': 'Agenda',
        'tools:calendar': 'Calendário',
        'tools:downloads': 'Downloads',
        'tools:services': 'Serviços',
        'tools:tourniquet': 'Torniquete',
    },
    'Documentos': {
        'documents:orders': 'Compras',
        'documents:payments': 'Pagamentos',
        'documents:guias': 'Guias de Transporte TPV',
        'documents:sales': 'Faturação',
        'documents:tickets': 'Senhas',
        'documents:cc': 'Documentos Pendentes',
    },
    'Configuração': {
        'config:pricetags': 'Etiquetas',
        'config:taxes': 'ISP',
        'config:prices_combustivel': 'Preços Combustíveis',
        'config:nozzles': 'Associar Mangueiras a Produtos',
        'config:series': 'Séries',
        'config:veeder_root': 'Veeder-Root',
        'config:employees': 'Gestão de utilizadores',
        'config:categories': 'Gestão de Categorias',
        'config:segmentacao': 'Gestão de Segmentações',
    },
    'POS': {
        'pos:customers': 'Clientes',
        'pos:tickets': 'Senhas',
        'pos:inventory': 'Inventário',
    }
});

class MenuPermissions extends Component {
  static contextType = UserContext;

  static propTypes = {
    cookies: instanceOf(Cookies).isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
      company: null,
      savingModule: {},
    };

    this.moduleCategories = getModuleCategories();
    this.loadFunction = this.loadFunction.bind(this);
    this.fetchCompany = this.fetchCompany.bind(this);
    this.updateModulePermission = this.updateModulePermission.bind(this);
    this.toggleCategory = this.toggleCategory.bind(this);
    this.toggleAllCompanyPermissions = this.toggleAllCompanyPermissions.bind(this);
  }

  loadFunction() {
    if (!this.state.loaded) {
      this.fetchCompany();
    }
  }

  async fetchCompany() {
    const profile = this.context;
    
    if (!profile.idEmpresaSec) {
      this.setState({ loaded: true });
      return;
    }

    try {
      const response = await empresasService.getById(profile.idEmpresaSec);
      
      if (response && response.idempresa) {
        const companyData = {
          ...response,
          modulesArray: response.modules ? response.modules.split(';').filter(m => m) : []
        };
        
        this.setState({ 
          company: companyData,
          loaded: true 
        });
      } else {
        this.setState({ loaded: true });
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      NotificationManager.error('Erro ao carregar empresa. Por favor, recarregue a página.', 'Erro', 5000);
      this.setState({ loaded: true });
    }
  }

  async updateModulePermission(idempresa, module, value) {
    const key = `${idempresa}-${module}`;
    this.setState(prevState => ({
      savingModule: { ...prevState.savingModule, [key]: true }
    }));

    try {
      await menuService.updateModulePermission(idempresa, module, value);

      // Update local state
      this.setState(prevState => {
        if (prevState.company && prevState.company.idempresa === idempresa) {
          let newModulesArray;
          if (value) {
            // Add module if not present
            newModulesArray = prevState.company.modulesArray.includes(module)
              ? prevState.company.modulesArray
              : [...prevState.company.modulesArray, module];
          } else {
            // Remove module
            newModulesArray = prevState.company.modulesArray.filter(m => m !== module);
          }
          return {
            company: { ...prevState.company, modulesArray: newModulesArray },
            savingModule: { ...prevState.savingModule, [key]: false }
          };
        }
        return {
          savingModule: { ...prevState.savingModule, [key]: false }
        };
      });
      
      NotificationManager.success('Permissão atualizada com sucesso', 'Sucesso', 3000);
    } catch (error) {
      console.error('Error updating permission:', error);
      NotificationManager.error('Erro ao atualizar permissão. Por favor, tente novamente.', 'Erro', 5000);
      this.setState(prevState => ({
        savingModule: { ...prevState.savingModule, [key]: false }
      }));
    }
  }

  async toggleCategory(idempresa, categoryName, event) {
    event.stopPropagation();
    event.preventDefault();

    const { company } = this.state;
    if (!company || company.idempresa !== idempresa) return;

    const categoryModules = Object.keys(this.moduleCategories[categoryName]);
    const checkedCount = categoryModules.filter(module => 
      company.modulesArray.includes(module)
    ).length;

    // Se tem algum módulo ativo, desativa todos. Se não tem nenhum, ativa todos.
    const shouldEnable = checkedCount === 0;

    // Show loading for category
    const loadingKey = `${idempresa}-category-${categoryName}`;
    this.setState(prevState => ({
      savingModule: { ...prevState.savingModule, [loadingKey]: true }
    }));

    try {
      // Prepare bulk update: group modules that need to be updated
      const modulesToUpdate = {};
      categoryModules.forEach(module => {
        if ((company.modulesArray.includes(module)) !== shouldEnable) {
          modulesToUpdate[module] = shouldEnable;
        }
      });

      // Send single bulk request if there are modules to update
      if (Object.keys(modulesToUpdate).length > 0) {
        await menuService.updateMultipleModulePermissions(idempresa, modulesToUpdate);
      }

      // Update local state
      this.setState(prevState => {
        if (prevState.company && prevState.company.idempresa === idempresa) {
          let newModulesArray = [...prevState.company.modulesArray];
          
          categoryModules.forEach(module => {
            if (shouldEnable) {
              if (!newModulesArray.includes(module)) {
                newModulesArray.push(module);
              }
            } else {
              newModulesArray = newModulesArray.filter(m => m !== module);
            }
          });
          
          return {
            company: { ...prevState.company, modulesArray: newModulesArray },
            savingModule: { ...prevState.savingModule, [loadingKey]: false }
          };
        }
        return {
          savingModule: { ...prevState.savingModule, [loadingKey]: false }
        };
      });
      
      NotificationManager.success(
        shouldEnable ? 'Todos os módulos ativados' : 'Todos os módulos desativados', 
        'Sucesso', 
        3000
      );
    } catch (error) {
      console.error('Error toggling category:', error);
      NotificationManager.error('Erro ao atualizar permissões. Por favor, tente novamente.', 'Erro', 5000);
      this.setState(prevState => ({
        savingModule: { ...prevState.savingModule, [loadingKey]: false }
      }));
    }
  }

  async toggleAllCompanyPermissions(event) {
    event.stopPropagation();
    event.preventDefault();

    const { company } = this.state;
    if (!company) return;

    // Get all modules from all categories
    const allModules = Object.values(this.moduleCategories).reduce((acc, categoryModules) => {
      return [...acc, ...Object.keys(categoryModules)];
    }, []);

    const checkedCount = allModules.filter(module => 
      company.modulesArray.includes(module)
    ).length;

    // Se tem algum módulo ativo, desativa todos. Se não tem nenhum, ativa todos.
    const shouldEnable = checkedCount === 0;

    // Show loading for company
    const loadingKey = `${company.idempresa}-toggle-all`;
    this.setState(prevState => ({
      savingModule: { ...prevState.savingModule, [loadingKey]: true }
    }));

    try {
      // Prepare bulk update: group all modules that need to be updated
      const modulesToUpdate = {};
      allModules.forEach(module => {
        if ((company.modulesArray.includes(module)) !== shouldEnable) {
          modulesToUpdate[module] = shouldEnable;
        }
      });

      // Send single bulk request if there are modules to update
      if (Object.keys(modulesToUpdate).length > 0) {
        await menuService.updateMultipleModulePermissions(company.idempresa, modulesToUpdate);
      }

      // Update local state
      this.setState(prevState => {
        if (prevState.company && prevState.company.idempresa === company.idempresa) {
          const newModulesArray = shouldEnable ? [...allModules] : [];
          
          return {
            company: { ...prevState.company, modulesArray: newModulesArray },
            savingModule: { ...prevState.savingModule, [loadingKey]: false }
          };
        }
        return {
          savingModule: { ...prevState.savingModule, [loadingKey]: false }
        };
      });
      
      NotificationManager.success(
        shouldEnable ? 'Todas as permissões ativadas' : 'Todas as permissões desativadas', 
        'Sucesso', 
        3000
      );
    } catch (error) {
      console.error('Error toggling all permissions:', error);
      NotificationManager.error('Erro ao atualizar permissões. Por favor, tente novamente.', 'Erro', 5000);
      this.setState(prevState => ({
        savingModule: { ...prevState.savingModule, [loadingKey]: false }
      }));
    }
  }

  getActiveModulesCount(company) {
    return company.modulesArray.length;
  }

  getTotalModulesCount() {
    return Object.values(this.moduleCategories).reduce((acc, categoryModules) => {
      return acc + Object.keys(categoryModules).length;
    }, 0);
  }

  getCategoryActiveCount(company, categoryName) {
    if (!company) return 0;
    const categoryModules = Object.keys(this.moduleCategories[categoryName]);
    return categoryModules.filter(module => company.modulesArray.includes(module)).length;
  }

  render() {
    const { company, savingModule, loaded } = this.state;
    const { location, history } = this.props;

    return (
      <div>
        <Menu
          loadFunction={this.loadFunction}
          location={location}
          history={history}
        />
        <div className="fullContainer" style={{ maxWidth: '100%' }}>
          <div className="col-xs-12">
            <ol
              style={{
                textAlign: 'left',
                marginTop: '75px',
              }}
              className="breadcrumb"
            >
              <li>
                <a href="http://eposgestofinal.com/index.php">Portal</a>
              </li>
              <li className="active">Gestão de Permissões de Menu</li>
            </ol>
          </div>
        </div>
        <EmpresaSelect />
        <div className="fullContainer" style={{ maxWidth: '100%' }}>
          <div className="col-sm-12" style={{ textAlign: 'center' }}>
            <div className="panel panel-default">
              <div
                className="panel-heading"
                style={{
                  fontWeight: 'bold',
                  fontSize: '15pt',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '15px'
                }}
              >
                <div
                  style={{
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <i className="fas fa-lock"></i> Gestão de Permissões de Menu
                </div>
              </div>
              <div className="panel-body">
                {/* Loading State */}
                {!loaded && (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', color: '#3498db' }}></i>
                    <p style={{ marginTop: '20px', color: '#666' }}>A carregar empresa...</p>
                  </div>
                )}

                {/* Company Card */}
                {loaded && company && (
                  <div id="companies-container">
                    <div className="company-card">
                      <div className="company-card-header">
                        <div>
                          <div className="company-card-title">
                            <i className="fas fa-briefcase"></i> {company.nomeempresa}
                          </div>
                          <div className="company-card-subtitle">
                            <i className="fas fa-user"></i> {company.username}
                            &nbsp;&nbsp;|&nbsp;&nbsp;
                            <i className="fas fa-credit-card"></i> {company.nif}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <span className="badge-count">
                            {this.getActiveModulesCount(company)}/{this.getTotalModulesCount()}
                          </span>
                          <div className="toggle-switch-container">
                            {savingModule[`${company.idempresa}-toggle-all`] && (
                              <i className="fas fa-spinner fa-spin" style={{ fontSize: '14px', color: 'white' }}></i>
                            )}
                            <label className="toggle-switch" title="Ativar/Desativar todas as permissões">
                              <input
                                type="checkbox"
                                checked={this.getActiveModulesCount(company) > 0}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  this.toggleAllCompanyPermissions(e);
                                }}
                                disabled={savingModule[`${company.idempresa}-toggle-all`]}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="company-card-body active">
                        {Object.entries(this.moduleCategories).map(([categoryName, modules]) => {
                          const categoryActiveCount = this.getCategoryActiveCount(company, categoryName);
                          const totalModules = Object.keys(modules).length;
                          const isCategoryActive = categoryActiveCount > 0;
                          const isCategoryLoading = savingModule[`${company.idempresa}-category-${categoryName}`];

                          return (
                            <div key={categoryName} className="module-category-section">
                              <div className="module-category-header">
                                <span>{categoryName}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span className="badge-count">
                                    {categoryActiveCount}/{totalModules}
                                  </span>
                                  <div className="toggle-switch-container">
                                    {isCategoryLoading && (
                                      <i className="fas fa-spinner fa-spin" style={{ fontSize: '14px', color: 'white' }}></i>
                                    )}
                                    <label className="toggle-switch" title="Toggle All">
                                      <input
                                        type="checkbox"
                                        checked={isCategoryActive}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          this.toggleCategory(company.idempresa, categoryName, e);
                                        }}
                                        disabled={isCategoryLoading}
                                      />
                                      <span className="toggle-slider"></span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                              {isCategoryActive && (
                                <div className="module-category-content">
                                  {Object.entries(modules).map(([moduleKey, moduleName]) => {
                                    const isChecked = company.modulesArray.includes(moduleKey);
                                    const isSaving = savingModule[`${company.idempresa}-${moduleKey}`];

                                    return (
                                      <div key={moduleKey} className="module-item">
                                        <span className="module-item-name">{moduleName}</span>
                                        <div className="toggle-switch-container">
                                          {isSaving && (
                                            <i className="fas fa-spinner fa-spin" style={{ fontSize: '14px' }}></i>
                                          )}
                                          <label className="toggle-switch">
                                            <input
                                              type="checkbox"
                                              checked={isChecked}
                                              onChange={(e) => this.updateModulePermission(
                                                company.idempresa, 
                                                moduleKey, 
                                                e.target.checked
                                              )}
                                              disabled={isSaving}
                                            />
                                            <span className="toggle-slider"></span>
                                          </label>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* No company selected */}
                {loaded && !company && (
                  <div className="no-results">
                    <i className="fas fa-info-circle"></i>
                    <p>Selecione uma empresa para gerir as permissões de menu.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withCookies(withRouter(MenuPermissions));

